import * as React from "react";
import { Home, Package, MapPin, ChevronRight, Sparkles, ArrowRight, AlertCircle } from "lucide-react";
import type { OnboardingGoalOption } from "@homethyst/shared/schemas";
import { onboardingGoalOptions, STRESS_SPACE_OPTIONS } from "@homethyst/shared/schemas";
import { cn } from "@/lib/utils.js";
import { LegalLinks } from "@/components/LegalLinks.js";
import LoginPage from "@/features/auth/LoginPage.js";

const GOAL_LABELS: Record<OnboardingGoalOption, { label: string; emoji: string }> = {
  organize_home: { label: "Organize my home", emoji: "🏠" },
  track_stuff: { label: "Track my stuff", emoji: "📦" },
  prepare_move: { label: "Prepare for a move", emoji: "🚚" },
  insurance_docs: { label: "Insurance documentation", emoji: "🛡️" },
  declutter: { label: "Declutter my space", emoji: "✨" },
  find_things: { label: "Find things faster", emoji: "🔍" },
};

function SpacesMockup() {
  return (
    <div className="bg-neutral rounded-xl p-4 mb-6 text-xs space-y-1.5">
      <div className="flex items-center gap-2 text-[11px] font-medium text-gray-500 mb-2">
        <MapPin className="w-3.5 h-3.5 text-primary" />Spaces
      </div>
      {[
        { icon: "🏠", name: "My Home", type: "home", depth: 0 },
        { icon: "🛋️", name: "Living Room", type: "room", depth: 1 },
        { icon: "🍳", name: "Kitchen", type: "room", depth: 1 },
        { icon: "📦", name: "Pantry", type: "container", depth: 2 },
        { icon: "🛏️", name: "Bedroom", type: "room", depth: 1 },
        { icon: "👔", name: "Closet", type: "container", depth: 2 },
      ].map((s, i) => (
        <div key={i} style={{ paddingLeft: s.depth * 16 }} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white/60">
          <span className="text-sm">{s.icon}</span>
          <span className="font-medium text-gray-700">{s.name}</span>
          <span className="ml-auto text-[9px] text-gray-400 uppercase">{s.type}</span>
        </div>
      ))}
    </div>
  );
}

function InventoryMockup() {
  return (
    <div className="bg-neutral rounded-xl p-4 mb-6 text-xs space-y-2">
      <div className="flex items-center gap-2 text-[11px] font-medium text-gray-500 mb-2">
        <Package className="w-3.5 h-3.5 text-primary" />Inventory
      </div>
      <div className="flex gap-1.5 mb-2">
        <div className="px-2 py-1 rounded-full bg-primary text-white text-[10px] font-medium">All</div>
        <div className="px-2 py-1 rounded-full bg-white text-gray-500 text-[10px]">Kitchen</div>
        <div className="px-2 py-1 rounded-full bg-white text-gray-500 text-[10px]">Bedroom</div>
      </div>
      {[
        { name: "Stand Mixer", space: "Kitchen", tag: "Appliances", photo: true },
        { name: "Winter Jacket", space: "Closet", tag: "Clothing", photo: true },
        { name: "Toolkit", space: "Garage", tag: "Tools", photo: false },
      ].map((item, i) => (
        <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-white/80">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${item.photo ? "bg-amethyst-soft" : "bg-gray-100"}`}>
            {item.photo ? "📷" : "📦"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-700 truncate">{item.name}</div>
            <div className="text-[10px] text-gray-400">📍 {item.space} · 🏷️ {item.tag}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function WorkflowsMockup() {
  return (
    <div className="bg-neutral rounded-xl p-4 mb-6 text-xs space-y-2">
      <div className="flex items-center gap-2 text-[11px] font-medium text-gray-500 mb-2">
        <Sparkles className="w-3.5 h-3.5 text-primary" />Organize
      </div>
      {[
        { title: "Weekly Kitchen Reset", freq: "Weekly", due: "Tomorrow", progress: 0.33, tasks: ["Clear counters", "Check expiry dates", "Wipe appliances"] },
        { title: "Monthly Closet Review", freq: "Monthly", due: "Apr 20", progress: 0, tasks: ["Donate unused items", "Reorganize shelves"] },
      ].map((flow, i) => (
        <div key={i} className="p-3 rounded-lg bg-white/80 space-y-2">
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-700">{flow.title}</span>
            <span className="text-[9px] text-gray-400">{flow.freq} · Due {flow.due}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full">
            <div className="h-full bg-primary rounded-full" style={{ width: `${flow.progress * 100}%` }} />
          </div>
          <div className="space-y-1">
            {flow.tasks.map((t, j) => (
              <div key={j} className="flex items-center gap-2">
                <div className={`w-3.5 h-3.5 rounded-full border ${j < flow.progress * flow.tasks.length ? "bg-primary border-primary" : "border-gray-300"} flex items-center justify-center`}>
                  {j < flow.progress * flow.tasks.length && <span className="text-white text-[7px]">✓</span>}
                </div>
                <span className="text-gray-600">{t}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const TOUR_SLIDES = [
  {
    icon: MapPin,
    title: "Map your spaces",
    desc: "Create a hierarchy of your home — floors, rooms, closets, drawers. Know exactly where everything lives.",
    mockup: SpacesMockup,
  },
  {
    icon: Package,
    title: "Catalog your items",
    desc: "Add items manually or snap a photo and let AI identify them. Search, filter, and never lose track again.",
    mockup: InventoryMockup,
  },
  {
    icon: Sparkles,
    title: "Stay on top of things",
    desc: "Set up organizing goals, try new placements, and build routines to keep your home the way you want it.",
    mockup: WorkflowsMockup,
  },
];

type Step = "goals" | "stress_spaces" | "tour" | "signup";

export default function OnboardingFlow() {
  const [step, setStep] = React.useState<Step>(() =>
    localStorage.getItem("ht_onboarding_seen") ? "signup" : "goals"
  );
  const [goals, setGoals] = React.useState<OnboardingGoalOption[]>([]);
  const [stressSpaces, setStressSpaces] = React.useState<{ room: string; reason: string }[]>([]);
  const [tourIndex, setTourIndex] = React.useState(0);

  const finish = (nextStep: Step) => {
    localStorage.setItem("ht_onboarding_seen", "1");
    setStep(nextStep);
  };

  const toggleGoal = (g: OnboardingGoalOption) =>
    setGoals((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));

  const toggleStressSpace = (room: string) => {
    setStressSpaces((prev) => {
      const existing = prev.find((s) => s.room === room);
      if (existing) return prev.filter((s) => s.room !== room);
      return [...prev, { room, reason: "" }];
    });
  };

  const setStressReason = (room: string, reason: string) => {
    setStressSpaces((prev) => prev.map((s) => s.room === room ? { ...s, reason } : s));
  };

  if (step === "signup") return <LoginPage goals={goals} stressSpaces={stressSpaces} onBackToOnboarding={() => { localStorage.removeItem("ht_onboarding_seen"); setStep("goals"); }} />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral p-4">
      <div className="w-full max-w-md">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {(["goals", "stress_spaces", "tour"] as const).map((s) => (
            <div key={s} className={cn("w-2 h-2 rounded-full transition-colors", step === s ? "bg-primary" : "bg-gray-300")} />
          ))}
        </div>

        {step === "goals" && (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-border/30 p-8">
            <div className="text-center mb-6">
              <Home className="w-8 h-8 text-primary mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-gray-900 font-headline">Welcome to Homethyst</h1>
              <p className="text-sm text-gray-500 mt-2">What brings you here? Select all that apply.</p>
            </div>

            <div className="space-y-3">
              {onboardingGoalOptions.map((g) => (
                <button
                  key={g}
                  onClick={() => toggleGoal(g)}
                  className={cn("w-full flex items-center gap-3 p-4 rounded-xl border text-left transition-colors",
                    goals.includes(g)
                      ? "border-primary bg-amethyst-soft text-gray-900"
                      : "border-neutral-border/40 text-gray-600 hover:border-gray-300"
                  )}
                >
                  <span className="text-xl">{GOAL_LABELS[g].emoji}</span>
                  <span className="text-sm font-medium">{GOAL_LABELS[g].label}</span>
                  {goals.includes(g) && <span className="ml-auto text-primary text-sm">✓</span>}
                </button>
              ))}
            </div>

            <button
              onClick={() => setStep("stress_spaces")}
              className="w-full mt-6 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-amethyst-ink flex items-center justify-center gap-1"
            >
              Continue <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => finish("signup")} className="w-full mt-2 py-2 text-xs text-gray-500 hover:text-gray-700">
              Skip to sign up
            </button>
          </div>
        )}

        {step === "stress_spaces" && (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-border/30 p-8">
            <div className="text-center mb-6">
              <AlertCircle className="w-8 h-8 text-primary mx-auto mb-3" />
              <h1 className="text-xl font-bold text-gray-900 font-headline">Which spaces stress you most?</h1>
              <p className="text-sm text-gray-500 mt-2">Select any spaces that feel chaotic or overwhelming. We'll help you start there.</p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {STRESS_SPACE_OPTIONS.map((opt) => {
                const selected = stressSpaces.some((s) => s.room === opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleStressSpace(opt.value)}
                    className={cn(
                      "flex items-center gap-2 p-3 rounded-xl border text-left transition-all text-sm",
                      selected
                        ? "border-primary bg-amethyst-soft text-gray-900"
                        : "border-neutral-border/40 text-gray-600 hover:border-gray-300"
                    )}
                  >
                    <span className="text-base">{opt.emoji}</span>
                    <span className="font-medium text-xs">{opt.label}</span>
                    {selected && <span className="ml-auto text-primary text-[10px]">✓</span>}
                  </button>
                );
              })}
            </div>

            {/* Show "why" inputs for selected spaces */}
            {stressSpaces.length > 0 && (
              <div className="space-y-3 mt-5 pt-5 border-t border-neutral-border/20">
                <p className="text-xs text-gray-500 font-medium">What's frustrating about these? <span className="text-gray-400">(optional)</span></p>
                {stressSpaces.map((ss) => {
                  const opt = STRESS_SPACE_OPTIONS.find((o) => o.value === ss.room);
                  return (
                    <div key={ss.room} className="flex items-start gap-2">
                      <span className="text-sm mt-2 shrink-0">{opt?.emoji}</span>
                      <input
                        value={ss.reason}
                        onChange={(e) => setStressReason(ss.room, e.target.value)}
                        placeholder={`Why does the ${opt?.label?.toLowerCase()} stress you?`}
                        aria-label={`Why does the ${opt?.label?.toLowerCase()} stress you?`}
                        className="flex-1 px-3 py-2 rounded-lg border border-neutral-border/30 text-xs focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none"
                        maxLength={200}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setStep("tour")}
              className="w-full mt-6 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-amethyst-ink flex items-center justify-center gap-1"
            >
              Continue <ChevronRight className="w-4 h-4" />
            </button>
            <div className="flex justify-between mt-2">
              <button onClick={() => setStep("goals")} className="py-2 text-xs text-gray-500 hover:text-gray-700">
                ← Back
              </button>
              <button onClick={() => setStep("tour")} className="py-2 text-xs text-gray-500 hover:text-gray-700">
                Skip
              </button>
            </div>
          </div>
        )}

        {step === "tour" && (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-border/30 p-8">
            {(() => {
              const slide = TOUR_SLIDES[tourIndex];
              const Icon = slide.icon;
              const Mockup = slide.mockup;
              return (
                <>
                  <div className="text-center mb-5">
                    <Icon className="w-8 h-8 text-primary mx-auto mb-3" />
                    <h2 className="text-xl font-bold text-gray-900 font-headline">{slide.title}</h2>
                    <p className="text-sm text-gray-500 mt-2">{slide.desc}</p>
                  </div>

                  <Mockup />

                  {/* Slide dots */}
                  <div className="flex justify-center gap-2 mb-5">
                    {TOUR_SLIDES.map((_, i) => (
                      <button key={i} onClick={() => setTourIndex(i)} className={`w-2 h-2 rounded-full transition-colors ${i === tourIndex ? "bg-primary" : "bg-gray-300"}`} />
                    ))}
                  </div>

                  {tourIndex < TOUR_SLIDES.length - 1 ? (
                    <button
                      onClick={() => setTourIndex(tourIndex + 1)}
                      className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-amethyst-ink flex items-center justify-center gap-1"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => finish("signup")}
                      className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-amethyst-ink flex items-center justify-center gap-1"
                    >
                      Get Started <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => finish("signup")} className="w-full mt-2 py-2 text-xs text-gray-500 hover:text-gray-700">
                    Skip to sign up
                  </button>
                  <button onClick={() => { setStep("stress_spaces"); setTourIndex(0); }} className="w-full py-2 text-xs text-gray-500 hover:text-gray-700">
                    ← Back
                  </button>
                </>
              );
            })()}
          </div>
        )}

        <LegalLinks className="mt-6" />
      </div>
    </div>
  );
}

